import config from '@cof/ukweb-config/lib/clientConfig';
import { AGGREGATOR_CONSUMER_NAMES, AGGREGATOR_WITH_LOGO, API_PARTNERS, HOSTED_PARTNERS } from './constants';
import { FORM_SECTIONS } from '../pages/QuickCheck/Components/FormIDs';
import type { FormValues } from '../types';
import { ReactNode } from 'react';

export const isHosted = (brand: unknown): boolean => {
    return HOSTED_PARTNERS.some((partner) => partner === brand);
};

export const hasAggregatorLogo = (consumerName: unknown): boolean => {
    return AGGREGATOR_WITH_LOGO.some((name) => name === consumerName);
};

export const isAggregatorConsumer = (consumerName: unknown): boolean => {
    return AGGREGATOR_CONSUMER_NAMES.some((name) => name === consumerName);
};

export const isApiPartner = (brand: unknown): boolean => {
    return API_PARTNERS.some((partner) => partner === brand);
};

export const isPartnerOrAggsEdit = (brand: unknown, aggregatorConsumerName: unknown): boolean =>
    isApiPartner(brand) || isAggregatorConsumer(aggregatorConsumerName);

// TODO - Clean up - remove this
export const useSinglePage = (brand: unknown, consumerName: unknown): boolean => {
    return isApiPartner(brand) || isAggregatorConsumer(consumerName);
};

export const lookupWithDefault = <T extends Record<string, unknown>>(
    table: T,
    key: string,
    defaultKey = 'default'
): unknown => table[key] || table[defaultKey];

export const addSParameterToLinkUrl = ({ url, sParameter }: { url: string; sParameter?: string }): string => {
    const urlObj = new URL(url);
    if (sParameter) {
        urlObj.searchParams.set('s', sParameter);
    }
    return urlObj.toString();
};

export const maskParam = (url: string, param: string, maskValue = 'MASKED'): string => {
    const urlObj = new URL(url);
    const paramValue = urlObj.searchParams.get(param);
    if (paramValue) {
        if (param === 'quotation') {
            paramValue.length > 16
                ? urlObj.searchParams.set(param, 'STANDARD_TOKEN')
                : urlObj.searchParams.set(param, 'SHORT_TOKEN');
        } else urlObj.searchParams.set(param, maskValue);
    }
    return urlObj.toString();
};

export const maskAllHashParams = (url: string): string => url.split('#')[0];

/**
 * Returns the value of a cookie
 * @param {string} k - The cookie key
 * @returns {string|undefined} The cookie value
 * See {@link https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie#example_5_check_a_cookie_existence}
 */
export const parseCookie = (k: string): string | undefined => {
    return document.cookie
        .split(';')
        .find((x) => x.trim().startsWith(`${k}=`))
        ?.split('=')[1];
};

export const removeAllWhitespace = (val: string): string => val.replace(/\s+/g, '');

export const trimWhitespace = (val: string): string => val.trim();

export const sendQuantumEvent = (
    eventID: number,
    conversion: number,
    primaryValue: boolean,
    attributes?: Record<string, unknown>
): void => {
    if (config.get('quantumMetric.isEnabled') && window.QuantumMetricAPI?.isOn()) {
        try {
            window.QuantumMetricAPI?.sendEvent(eventID, conversion, primaryValue, attributes);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e instanceof Error ? e.message : e);
        }
    }
};

export const postcodeRegex =
    /^(?!im|je|gy|IM|JE|GY|iM|jE|gY|Im|Je|Gy)[a-zA-Z]{1,2}[0-9][0-9a-zA-Z]?\s?[0-9][a-zA-Z]{2}$/;

export const houseFlatNumberRegex = /^[A-Za-z0-9',.& /-]+$/;

export const getAriaDescribedBy = (
    id: string | undefined,
    submitAttempted: boolean,
    additionalInfoIDs: string[] = [],
    errors: Record<string, unknown> = {}
): string | undefined => {
    const errorMessage = `${id}-error-message`;
    const hasError = submitAttempted && id !== undefined && errors[id];

    if (additionalInfoIDs.length === 0) {
        return hasError ? errorMessage : undefined;
    }
    const base = additionalInfoIDs.join(' ');
    return hasError ? `${base} ${errorMessage}` : base;
};

export type NormalizedFormValues = Record<string, string | boolean>;

export const normalizeFormValues = (values: FormValues | null | undefined): NormalizedFormValues => {
    if (!values) return {};
    const normalized: NormalizedFormValues = { ...values };
    const checkboxFields = [FORM_SECTIONS.Permissions.Marketing, FORM_SECTIONS.Permissions.FutureSoftSearch];
    checkboxFields.forEach((field) => {
        if (field in normalized) {
            normalized[field] = normalized[field] === true || normalized[field] === 'true';
        }
    });
    return normalized;
};

export const nodeToString = (node: ReactNode) => {
    return typeof node === 'string' ? node : '';
};
